import { Field, FieldCheckbox, FieldInput, FieldLabel } from '@ibe/ui';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import { useAuth } from '../../../auth/auth-context.js';

/**
 * A searchable, collapsible permission tree.
 *
 * A flat grid of 30-40 checkboxes is hard to scan, so the permissions are
 * grouped by the dotted resource part of their canonical name and rendered as
 * nested, expandable sections. The reader can search, expand/collapse all,
 * select all visible, and select or clear each branch.
 */

interface PermissionLeaf {
  readonly value: string;
  readonly action: string;
}

interface PermissionTreeNode {
  readonly key: string;
  readonly label: string;
  readonly children: PermissionTreeNode[];
  readonly permissions: PermissionLeaf[];
}

function permissionParts(permission: string): {
  readonly path: readonly string[];
  readonly action: string;
} {
  const segments = permission.split('.');
  return {
    path: segments.slice(0, -1),
    action: segments[segments.length - 1] ?? permission,
  };
}

function humanise(name: string): string {
  return name.replace(/([A-Z])/g, ' $1').trim();
}

function buildTree(permissions: readonly string[]): PermissionTreeNode {
  const root: PermissionTreeNode = {
    key: '__root',
    label: 'Permissions',
    children: [],
    permissions: [],
  };

  for (const permission of permissions) {
    const { path, action } = permissionParts(permission);
    let node = root;

    for (const segment of path) {
      const next = node.children.find((child) => child.label === segment);
      if (next !== undefined) {
        node = next;
      } else {
        const created: PermissionTreeNode = {
          key: `${node.key}.${segment}`,
          label: segment,
          children: [],
          permissions: [],
        };
        node.children.push(created);
        node = created;
      }
    }

    node.permissions.push({ value: permission, action });
  }

  return root;
}

function valuesInNode(node: PermissionTreeNode, values: string[]): void {
  for (const permission of node.permissions) {
    values.push(permission.value);
  }
  for (const child of node.children) {
    valuesInNode(child, values);
  }
}

function allGroupKeys(node: PermissionTreeNode, keys: string[] = []): string[] {
  for (const child of node.children) {
    keys.push(child.key);
    allGroupKeys(child, keys);
  }
  return keys;
}

function firstLevelKeys(node: PermissionTreeNode): string[] {
  return node.children.map((child) => child.key);
}

function allSelected(node: PermissionTreeNode, selected: ReadonlySet<string>): boolean {
  const groupValues: string[] = [];
  valuesInNode(node, groupValues);
  if (groupValues.length === 0) return false;
  return groupValues.every((value) => selected.has(value));
}

export interface PermissionTreeProps {
  readonly selected: readonly string[];
  readonly onChange: (selected: readonly string[]) => void;
  /** Announced error for the whole control. */
  readonly error?: string | undefined;
}

function TreeNode({
  node,
  selected,
  onChange,
  expanded,
  onToggle,
}: {
  readonly node: PermissionTreeNode;
  readonly selected: ReadonlySet<string>;
  readonly onChange: (selected: ReadonlySet<string>) => void;
  readonly expanded: ReadonlySet<string>;
  readonly onToggle: (key: string) => void;
}): React.JSX.Element {
  const open = expanded.has(node.key);
  const hasChildren = node.children.length > 0;
  const hasPermissions = node.permissions.length > 0;
  const groupSelected = allSelected(node, selected);

  const toggleGroup = (): void => {
    const groupValues: string[] = [];
    valuesInNode(node, groupValues);
    if (groupValues.length === 0) return;

    const next = new Set(selected);
    if (groupSelected) {
      for (const value of groupValues) next.delete(value);
    } else {
      for (const value of groupValues) next.add(value);
    }
    onChange(next);
  };

  const toggleLeaf = (value: string, checked: boolean): void => {
    const next = new Set(selected);
    if (checked) next.add(value);
    else next.delete(value);
    onChange(next);
  };

  const groupId = useId();

  return (
    <li className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => {
            onToggle(node.key);
          }}
          aria-expanded={open}
          aria-controls={groupId}
          className="inline-flex items-center gap-1 text-sm font-medium hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {open ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
          {humanise(node.label)}
        </button>

        {hasChildren || hasPermissions ? (
          <button
            type="button"
            onClick={toggleGroup}
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            {groupSelected ? 'Clear all' : 'Select all'}
          </button>
        ) : null}
      </div>

      {open ? (
        <div id={groupId} className="pl-5">
          {hasChildren ? (
            <ul className="space-y-1">
              {node.children.map((child) => (
                <TreeNode
                  key={child.key}
                  node={child}
                  selected={selected}
                  onChange={onChange}
                  expanded={expanded}
                  onToggle={onToggle}
                />
              ))}
            </ul>
          ) : null}

          {hasPermissions ? (
            <div className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2 md:grid-cols-3">
              {node.permissions.map((permission) => (
                <Field key={permission.value}>
                  <FieldCheckbox
                    checked={selected.has(permission.value)}
                    onCheckedChange={(checked) => {
                      toggleLeaf(permission.value, checked);
                    }}
                  >
                    {permission.action}
                  </FieldCheckbox>
                </Field>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function PermissionTree({
  selected,
  onChange,
  error,
}: PermissionTreeProps): React.JSX.Element {
  const { user } = useAuth();
  const permissions = user?.permissions;
  const [query, setQuery] = useState('');
  const [manualExpanded, setManualExpanded] = useState<ReadonlySet<string> | null>(null);

  const filteredPermissions = useMemo(() => {
    const source = permissions ?? [];
    const normalised = query.trim().toLowerCase();
    if (normalised === '') return source;
    return source.filter((permission) => permission.toLowerCase().includes(normalised));
  }, [permissions, query]);

  const tree = useMemo(() => buildTree(filteredPermissions), [filteredPermissions]);

  const baseExpanded = useMemo(() => {
    if (query.trim() === '') {
      return new Set(firstLevelKeys(tree));
    }
    return new Set(allGroupKeys(tree));
  }, [tree, query]);

  const expanded = manualExpanded ?? baseExpanded;

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const totalPermissions = useMemo(() => {
    const source = permissions ?? [];
    const root = buildTree(source);
    const values: string[] = [];
    valuesInNode(root, values);
    return values.length;
  }, [permissions]);

  const handleChange = (next: ReadonlySet<string>): void => {
    onChange([...next].sort((left, right) => left.localeCompare(right)));
  };

  const selectAllVisible = (): void => {
    const values: string[] = [];
    valuesInNode(tree, values);
    handleChange(new Set([...selectedSet, ...values]));
  };

  const clearAll = (): void => {
    const values: string[] = [];
    valuesInNode(tree, values);
    const next = new Set(selectedSet);
    for (const value of values) next.delete(value);
    handleChange(next);
  };

  const expandAll = (): void => {
    setManualExpanded(new Set(allGroupKeys(tree)));
  };

  const collapseAll = (): void => {
    setManualExpanded(new Set());
  };

  const handleQueryChange = (value: string): void => {
    setQuery(value);
    setManualExpanded(null);
  };

  const hasAny = totalPermissions > 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field>
          <FieldLabel>Find a permission</FieldLabel>
          <FieldInput
            type="search"
            placeholder="Search by permission name..."
            value={query}
            onChange={(event) => {
              handleQueryChange(event.currentTarget.value);
            }}
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={expandAll}
          disabled={!hasAny}
          className="text-xs font-medium text-primary underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={collapseAll}
          disabled={!hasAny}
          className="text-xs font-medium text-primary underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
        >
          Collapse all
        </button>
        <button
          type="button"
          onClick={selectAllVisible}
          disabled={!hasAny}
          className="text-xs font-medium text-primary underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
        >
          Select all visible
        </button>
        <button
          type="button"
          onClick={clearAll}
          disabled={selected.length === 0}
          className="text-xs font-medium text-destructive underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
        >
          Clear all
        </button>
        <span className="ml-auto text-sm text-muted-foreground">
          {selected.length} of {totalPermissions} selected
        </span>
      </div>

      {hasAny ? (
        <ul role="tree" className="space-y-2" aria-label="Permission tree">
          {tree.children.map((child) => (
            <TreeNode
              key={child.key}
              node={child}
              selected={selectedSet}
              onChange={handleChange}
              expanded={expanded}
              onToggle={(key) => {
                const next = new Set(expanded);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                setManualExpanded(next);
              }}
            />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">You have no permissions to assign.</p>
      )}

      {error !== undefined && error !== '' ? (
        <p className="text-sm font-medium text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
